<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';
import authAPI from 'dashboard/api/auth';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const getBranches = useMapGetter('branches/getBranches');
const getMyBranches = useMapGetter('branches/getMyBranches');
const selectedChat = useMapGetter('getSelectedChat');

const allBranches = computed(() => {
  return isAdmin.value ? getBranches.value : getMyBranches.value;
});

const activeBranchId = computed(() => uiSettings.value?.active_branch_id);

const activeBranch = computed(() => {
  if (!activeBranchId.value) return null;
  return allBranches.value.find(b => b.id === activeBranchId.value);
});

const showBranchSwitcher = computed(() => allBranches.value.length > 1);

const onChangeBranch = async branchId => {
  try {
    const newSettings = {
      ...uiSettings.value,
      active_branch_id: branchId,
    };
    await authAPI.updateUISettings({ uiSettings: newSettings });
  } catch (error) {
    // Continue with reload even if save fails
  }

  const chatBranchId = selectedChat.value?.meta?.branch?.id;
  if (branchId && chatBranchId && chatBranchId !== branchId) {
    window.location.href = `/app/accounts/${accountId.value}/dashboard`;
  } else {
    // Reload the current page to apply the branch filter
    window.location.reload();
  }
};
</script>

<template>
  <!-- eslint-disable-next-line vue/no-root-v-if -->
  <DropdownContainer v-if="showBranchSwitcher">
    <template #trigger="{ toggle, isOpen }">
      <button
        v-if="isCollapsed"
        class="grid flex-shrink-0 place-content-center p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :title="
          activeBranch
            ? activeBranch.name
            : $t('SIDEBAR.BRANCH_SELECTOR.ALL_BRANCHES')
        "
        @click="toggle"
      >
        <Icon icon="i-lucide-building-2" class="size-5" />
      </button>
      <button
        v-else
        class="flex items-center gap-2 justify-between w-full rounded-lg px-2 h-8"
        :class="[
          isOpen && 'bg-n-alpha-1',
          allBranches.length > 0
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="() => allBranches.length > 0 && toggle()"
      >
        <div class="flex items-center gap-2 min-w-0">
          <Icon
            icon="i-lucide-building-2"
            class="size-4 text-n-slate-11 flex-shrink-0"
          />
          <span
            class="text-xs font-medium leading-5 text-n-slate-11 truncate"
            aria-live="polite"
          >
            {{
              activeBranch
                ? activeBranch.name
                : $t('SIDEBAR.BRANCH_SELECTOR.ALL_BRANCHES')
            }}
          </span>
        </div>

        <span
          v-if="allBranches.length > 0"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody
      v-if="allBranches.length > 0 || isCollapsed"
      class="min-w-64 z-50"
    >
      <DropdownSection :title="$t('SIDEBAR.BRANCH_SELECTOR.SWITCH_BRANCH')">
        <DropdownItem class="cursor-pointer" @click="onChangeBranch(null)">
          <template #label>
            <span class="text-n-slate-12">{{
              $t('SIDEBAR.BRANCH_SELECTOR.ALL_BRANCHES')
            }}</span>
            <Icon
              v-show="!activeBranchId"
              icon="i-lucide-check"
              class="text-n-teal-11 size-5"
            />
          </template>
        </DropdownItem>
        <DropdownItem
          v-for="branch in allBranches"
          :id="`branch-${branch.id}`"
          :key="branch.id"
          class="cursor-pointer"
          @click="onChangeBranch(branch.id)"
        >
          <template #label>
            <div class="flex gap-2 items-center">
              <span class="text-n-slate-12 truncate" :title="branch.name">
                {{ branch.name }}
              </span>
            </div>
            <Icon
              v-show="branch.id === activeBranchId"
              icon="i-lucide-check"
              class="text-n-teal-11 size-5"
            />
          </template>
        </DropdownItem>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
